import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import FormatterLayout from "../components/FormatterLayout";
import AppLayout from "../components/AppLayout";
import ToolContent, { converterContent } from "../toolContent";
import * as conversion from "../converters/conversion.js";
import { load, save, remove } from "../storage";

const { FORMATS, canConvert, convert } = conversion;

const inputKey = (from) => `conv:${from}:input`;

const languageMap = {
  json: "json",
  xml: "xml",
  html: "html",
  yml: "yaml",
  css: "css",
  js: "javascript",
};

const labelOf = (key) => FORMATS.find((f) => f.key === key)?.label || key;

export default function Converter({ entries }) {
  const { type } = useParams();
  const navigate = useNavigate();

  const [from, setFrom] = useState(type?.split("-to-")[0] || "json");
  const [to, setTo] = useState(type?.split("-to-")[1] || "xml");
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const loadedFromRef = useRef(null);
  const sampleRef = useRef("");

  // Keep the URL in sync
  useEffect(() => {
    navigate(`/converter/${from}-to-${to}`, { replace: true });
  }, [from, to, navigate]);

  // If the current target isn't reachable from the source, pick a valid one
  useEffect(() => {
    if (!canConvert(from, to)) {
      const first = FORMATS.find((f) => canConvert(from, f.key));
      if (first) setTo(first.key);
    }
  }, [from, to]);

  // Seed the input: restore the saved buffer for this source, else its sample
  useEffect(() => {
    loadedFromRef.current = null;
    let cancelled = false;
    import(`../formatters/${from}.js`)
      .then((m) => {
        if (cancelled) return;
        const sample = typeof m.sample === "string" ? m.sample : "";
        sampleRef.current = sample;
        const saved = load(inputKey(from));
        setInput(saved != null ? saved : sample);
        loadedFromRef.current = from;
      })
      .catch(() => {
        loadedFromRef.current = from;
      });
    return () => {
      cancelled = true;
    };
  }, [from]);

  // Persist the input buffer per source format
  useEffect(() => {
    if (loadedFromRef.current !== from) return;
    save(inputKey(from), input);
  }, [input, from]);

  // Live conversion
  useEffect(() => {
    setOutput(convert(input, from, to));
  }, [input, from, to]);

  const resetToSample = () => {
    remove(inputKey(from));
    setInput(sampleRef.current);
  };

  const targets = FORMATS.filter((f) => canConvert(from, f.key));

  return (
    <AppLayout entries={entries} showRightSidebar={false}>
      <div className="converter-bar">
        <label>
          From
          <select value={from} onChange={(e) => setFrom(e.target.value)}>
            {FORMATS.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </select>
        </label>

        <span className="converter-arrow">→</span>

        <label>
          To
          <select value={to} onChange={(e) => setTo(e.target.value)}>
            {targets.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <FormatterLayout
        title={`${labelOf(from)} → ${labelOf(to)}`}
        kind="Converter"
        input={input}
        setInput={setInput}
        output={output}
        setOutput={setOutput}
        onFormat={() => setOutput(convert(input, from, to))}
        onReset={resetToSample}
        sourceLanguage={languageMap[from] || "plaintext"}
        targetLanguage={languageMap[to] || "plaintext"}
      />
      <ToolContent
        content={converterContent(from, labelOf(from), to, labelOf(to))}
      />
    </AppLayout>
  );
}
